<script setup>
import { router } from '@inertiajs/vue3'
import { useToast } from 'primevue/usetoast'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  user: { type: Object, required: true },
  guards: { type: Object, default: () => ({ is_self: false, is_last_admin: false }) },
})

const blockDisabled = computed(() => {
  if (props.guards.is_self) return true
  // Last-admin protection only blocks "block", not "unblock"
  if (props.guards.is_last_admin && !props.user.blocked) return true
  return false
})

const blockHint = computed(() => {
  if (props.guards.is_self) return 'messages.admin.user.cannot_block_self_hint'
  if (props.guards.is_last_admin && !props.user.blocked) return 'messages.admin.user.cannot_block_last_admin_hint'
  return null
})

const { t } = useI18n()
const toast = useToast()

const sending = ref({ reset: false, block: false })

const sendPasswordReset = () => {
  sending.value.reset = true
  router.post(route('admin.user.password-reset', props.user.id), {}, {
    preserveScroll: true,
    onSuccess: () => {
      toast.add({
        severity: 'success',
        summary: t('messages.updated'),
        detail: t('messages.admin.user.password_reset_sent_success', { email: props.user.email }),
        life: 4000,
      })
    },
    onError: () => {
      toast.add({
        severity: 'error',
        summary: t('messages.error'),
        detail: t('messages.admin.user.password_reset_sent_error'),
        life: 4000,
      })
    },
    onFinish: () => { sending.value.reset = false },
  })
}

const toggleBlock = () => {
  sending.value.block = true
  // Capture the pre-action state: by the time onSuccess fires, Inertia has
  // already refreshed `props.user.blocked` to the new value, so reading it
  // there would invert the toast.
  const wasBlocked = props.user.blocked
  const routeName = wasBlocked ? 'admin.user.unblock' : 'admin.user.block'
  router.post(route(routeName, props.user.id), {}, {
    preserveScroll: true,
    onSuccess: () => {
      toast.add({
        severity: 'success',
        summary: t('messages.updated'),
        detail: t(wasBlocked ? 'messages.admin.user.unblocked_success' : 'messages.admin.user.blocked_success'),
        life: 3000,
      })
    },
    onFinish: () => { sending.value.block = false },
  })
}
</script>

<template>
  <Card>
    <template #title>{{ $t('messages.admin.user.tabs.account') }}</template>
    <template #content>
      <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between gap-4">
          <div class="text-sm text-gray-600">{{ $t('messages.admin.user.password_reset_desc') }}</div>
          <Button outlined :disabled="sending.reset" :loading="sending.reset" @click="sendPasswordReset">
            {{ $t('messages.admin.user.send_password_reset') }}
          </Button>
        </div>
        <div class="flex items-center justify-between gap-4">
          <div class="flex flex-col text-sm text-gray-600">
            <span>{{ user.blocked ? $t('messages.admin.user.unblock_desc') : $t('messages.admin.user.block_desc') }}</span>
            <span v-if="blockHint" class="text-xs text-gray-400 italic">
              {{ $t(blockHint) }}
            </span>
          </div>
          <Button
            :outlined="!user.blocked"
            :severity="user.blocked ? 'success' : 'warn'"
            :disabled="sending.block || blockDisabled"
            :loading="sending.block"
            @click="toggleBlock"
          >
            {{ user.blocked ? $t('messages.admin.user.unblock') : $t('messages.admin.user.block') }}
          </Button>
        </div>
      </div>
    </template>
  </Card>
</template>
